<template>
  <div class="school-details-page">
    <headers />

    <!-- Breadcrumb 面包屑导航 -->
    <div class="details-sub-nav">
      <div class="breadcrumb-container">
        <span
          class="breadcrumb-item cursor-pointer"
          @click="$router.push({ path: '/school/index', query: { tab: 0 } })"
          >首页</span
        >
        <span class="breadcrumb-separator"></span>
        <span
          class="breadcrumb-item cursor-pointer"
          @click="$router.push({ path: '/school/index', query: { tab: 1 } })"
          >课程学习</span
        >
        <span class="breadcrumb-separator"></span>
        <span class="breadcrumb-item active">课程详情</span>
      </div>
    </div>

    <!-- Main Content Container 主版面 -->
    <div class="details-content-wrap">
      <!-- 课程大卡片区 (卡片上左右布局) -->
      <div class="details-header-card">
        <!-- 左侧视频播放器/大封皮 -->
        <div
          class="video-container"
          ref="videoContainer"
          @mousemove="handleMouseMove"
          @mouseleave="handleMouseLeave"
        >
          <video
            class="video-player"
            preload
            ref="videoPlayer"
            @play="onPlay"
            @pause="onPause"
            @ended="onVideoEnded"
            @timeupdate="onTimeUpdate"
            @loadedmetadata="onLoadedMetadata"
            @click="togglePlay"
            :muted="isMuted"
          >
            <source :src="videoUrl" type="video/mp4" />
          </video>
          
          <!-- B站风格自定义视频控制栏 -->
          <transition name="controls-fade">
            <div class="custom-video-controls" v-show="controlsVisible" @click.stop>
              <!-- 顶部横向进度条 -->
              <div 
                class="custom-progress-slider" 
                ref="progressBar" 
                @mousedown.stop="onProgressBarMouseDown"
                @click.stop="onProgressBarClick"
              >
                <div class="custom-progress-bg"></div>
                <div class="custom-progress-filled" :style="{ width: videoPlayPercent + '%' }"></div>
                <div class="custom-progress-handle" :style="{ left: videoPlayPercent + '%' }"></div>
              </div>

              <!-- 底部操作按钮行 -->
              <div class="custom-controls-bar">
                <!-- 左侧：播放、下一个、时间 -->
                <div class="controls-left">
                  <div class="control-btn play-btn" @click.stop="togglePlay">
                    <i :class="isPlaying ? 'el-icon-video-pause' : 'el-icon-video-play'"></i>
                  </div>
                  <div class="control-btn next-btn" v-if="actIndex < details.details.length - 1" @click.stop="changeAct(actIndex + 1)">
                    <i class="el-icon-skip-forward" style="font-size: 16px;"></i>
                  </div>
                  <div class="time-display">
                    <span class="current-time">{{ formatTime(videoCurrentTime) }}</span>
                    <span class="time-separator">/</span>
                    <span class="duration-time">{{ formatTime(videoDuration) }}</span>
                  </div>
                </div>

                <!-- 右侧：倍速、静音、全屏 -->
                <div class="controls-right">
                  <!-- 倍速调节 -->
                  <div
                    class="video-rate-control-wrap"
                    @mouseenter="showRateMenu = true"
                    @mouseleave="showRateMenu = false"
                  >
                    <transition name="rate-menu-fade">
                      <div class="rate-menu-panel" v-show="showRateMenu">
                        <div
                          v-for="rate in [2.0, 1.5, 1.25, 1.0, 0.75]"
                          :key="rate"
                          class="rate-menu-item"
                          :class="{ active: currentPlaybackRate === rate }"
                          @click="changePlaybackRate(rate)"
                        >
                          {{ rate }}x
                        </div>
                      </div>
                    </transition>
                    <div class="rate-trigger-btn">
                      {{
                        currentPlaybackRate === 1.0 ? "倍速" : currentPlaybackRate + "x"
                      }}
                    </div>
                  </div>

                  <!-- 静音调节 -->
                  <div 
                    class="custom-volume-control" 
                    @mouseenter="showVolumeSlider = true"
                    @mouseleave="showVolumeSlider = false"
                  >
                    <transition name="volume-fade">
                      <div class="volume-slider-panel" v-show="showVolumeSlider" @click.stop>
                        <div class="volume-track" ref="volumeTrack" @mousedown.stop="onVolumeTrackMouseDown" @click.stop="onVolumeTrackClick">
                          <div class="volume-fill" :style="{ height: volume * 100 + '%' }"></div>
                          <div class="volume-handle" :style="{ bottom: volume * 100 + '%' }"></div>
                        </div>
                      </div>
                    </transition>
                    <div class="control-btn volume-btn" @click.stop="toggleMute">
                      <i :class="isMuted ? 'el-icon-turn-off-microphone' : 'el-icon-microphone'"></i>
                    </div>
                  </div>

                  <!-- 全屏 -->
                  <div class="control-btn fullscreen-btn" @click.stop="toggleFullScreen">
                    <i :class="isFullScreen ? 'el-icon-copy-document' : 'el-icon-full-screen'"></i>
                  </div>
                </div>
              </div>
            </div>
          </transition>

          <div class="play-overlay" v-if="showBtn" @click="togglePlay">
            <img class="play-btn-icon" src="@/assets/images/play.png" />
          </div>
        </div>

        <!-- 右侧信息描述与操作 -->
        <div class="info-container">
          <div class="info-top">
            <div class="info-title-row">
              <div class="title-indicator"></div>
              <div class="info-title textOverflowTwo">
                {{ details.name }}
              </div>
            </div>
            <!-- <div class="info-desc textOverflowThree">
              介绍：{{ details.remark || "" }}
            </div> -->
          </div>

          <div class="info-bottom">
            <!-- 日期 -->
            <div class="meta-item">
              <span class="meta-label">日期：</span>
              <span class="meta-value">{{
                details.createTime ? details.createTime.split(" ")[0] : ""
              }}</span>
            </div>

            <!-- 时长 -->
            <div class="meta-item">
              <span class="meta-label">时长：</span>
              <span class="meta-value">{{ formattedTotalDuration }}</span>
            </div>

            <!-- 进度条 -->
            <div class="progress-section">
              <div class="progress-text-row">
                <span class="progress-label">进度</span>
                <span class="progress-ratio"
                  >{{ completedChaptersCount }}/{{ totalChaptersCount }}</span
                >
              </div>
              <div class="progress-bar-bg">
                <div
                  class="progress-bar-fill"
                  :style="{ width: progressPercent + '%' }"
                ></div>
              </div>
            </div>

            <!-- 浏览 -->
            <div class="meta-item">
              <span class="meta-label">浏览：</span>
              <span class="meta-value">{{ details.studyNum || 0 }}</span>
            </div>

            <!-- 开始学习按钮 -->
            <div class="action-btn-row">
              <div class="start-learn-btn" @click="togglePlay">开始学习</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 下方课程目录详情区 -->
      <div class="directory-section">
        <div class="directory-list-card">
          <!-- 仿 tab 目录标题条 -->
          <div class="directory-tab-header">
            <div class="directory-tab-item">课程目录</div>
          </div>

          <!-- 章节目录视频列表 -->
          <div class="chapter-list-wrap">
            <div
              class="chapter-item"
              v-for="(item, index) in details.details"
              :key="index"
              @click="changeAct(index)"
              :class="{ active: actIndex === index }"
            >
              <div class="chapter-left">
                <span class="chapter-video-tag">视频 {{ index + 1 }}</span>
                <span
                  class="play-circle-icon"
                  :class="{ active: actIndex === index }"
                >
                  <i class="el-icon-video-play"></i>
                </span>
                <span class="chapter-name">{{ item.fileName }}</span>
              </div>

              <div class="chapter-right">
                <span class="chapter-duration">
                  时长: {{ formatToHHMMSS(item.durationNum) }}
                </span>
                <span class="last-view-badge completed" v-if="item.state == 1">
                  已看完
                </span>
                <span class="last-view-badge" v-else>
                  上次看到{{ formatSeconds(item.viewTime || 0) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 考试弹窗 -->
    <examModel v-if="examModel" :examModel="examModel">
      <div class="examModelContent">
        <div class="examModelTitle">恭喜你学完指定课程</div>
        <div class="examModelText">
          恭喜你学完指定课程，请开始结业考试吧！祝你好运……
        </div>
        <div class="examModelBtn">
          <div class="examModelBtnCancel" @click="examModel = false">取消</div>
          <div class="examModelBtnStart" @click="jumpExam">开始考试</div>
        </div>
      </div>
    </examModel>

    <footers />
  </div>
</template>

<script>
import headers from "@/components/header.vue";
import footers from "@/components/footer.vue";
import search from "@/components/search.vue";

import {
  courseInfoView,
  videoReadAdd,
  videoStudy,
  videoStudyAdd,
} from "@/api/lx";
import examModel from "./examModel.vue";

export default {
  name: "Procucts",
  components: {
    headers,
    footers,
    search,
    examModel,
  },
  data() {
    return {
      backGround: {
        L1: "rgba(0, 216, 195, 0.1)",
        L2: "rgba(240, 193, 11, 0.1)",
        L3: "rgba(71, 177, 226, 0.1)",
        L4: "rgba(147, 93, 228, 0.1)",
      },
      colorEnum: {
        L1: "#00D8C3",
        L2: "#F0C10B",
        L3: "#47B1E2",
        L4: "#935DE4",
      },
      actIndex: 0,
      showBtn: true,
      id: "",
      details: {},
      list: [],
      videoUrl: "",
      classifyName: "",
      classifyId: "",
      videoTime: 0, //视频时长
      enterTime: 0, // 进入页面时间
      studyDuration: 0, // 学习时长
      videoId: "", // 当前选择视频的id
      queryVideoId: "", // 路由传入要直接播放的视频id
      examModel: false, // 考试弹窗
      currentPlaybackRate: 1.0, // 当前播放倍速
      controlsVisible: true, // 视频内控制组件显隐
      showRateMenu: false, // 视频内倍速选择菜单显隐
      controlsTimer: null, // 控制隐藏的定时器
      isPlaying: false, // 是否正在播放
      videoCurrentTime: 0, // 视频当前播放的秒数
      videoDuration: 0, // 视频的总秒数
      isMuted: false, // 是否静音
      volume: 1.0, // 音量大小 (0 ~ 1)
      showVolumeSlider: false, // 音量滑块面板显示
      isFullScreen: false, // 是否全屏
      isDraggingProgress: false, // 是否正在拖动进度条
      isDraggingVolume: false, // 是否正在拖动音量
    };
  },
  computed: {
    // 累加视频列表中所有视频的时长，得出总时长
    formattedTotalDuration() {
      if (!this.details.details || !this.details.details.length) return "0秒";
      const totalSeconds = this.details.details.reduce(
        (sum, item) => sum + (item.durationNum || 0),
        0
      );
      return this.formatSeconds(totalSeconds);
    },
    // 已观看完的节数
    completedChaptersCount() {
      if (!this.details.details) return 0;
      return this.details.details.filter((d) => d.state === 1).length;
    },
    // 总节数
    totalChaptersCount() {
      if (!this.details.details) return 0;
      return this.details.details.length;
    },
    // 进度百分比
    progressPercent() {
      if (!this.totalChaptersCount) return 0;
      return (this.completedChaptersCount / this.totalChaptersCount) * 100;
    },
    // 自定义视频播放进度百分比
    videoPlayPercent() {
      if (!this.videoDuration) return 0;
      return (this.videoCurrentTime / this.videoDuration) * 100;
    },
  },
  mounted() {
    this.enterTime = new Date().getTime();
    document.addEventListener("fullscreenchange", this.onFullScreenChange);
  },
  beforeDestroy() {
    document.removeEventListener("fullscreenchange", this.onFullScreenChange);
    if (this.controlsTimer) {
      clearTimeout(this.controlsTimer);
    }
    // 页面卸载时计算学习时长
    const leaveTime = new Date().getTime();
    this.studyDuration = Math.floor((leaveTime - this.enterTime) / 1000);

    this.saveStudyDuration();
    this.examModel = false;
    console.log("beforeDestroy", this.actIndex);

    if (
      this.details.details &&
      this.details.details[this.actIndex] &&
      [0, 2].includes(this.details.details[this.actIndex].state)
    ) {
      this.saveVideoTime();
    }
  },
  created() {
    this.id = this.$route.query.id;
    this.classifyName = this.$route.query.classifyName;
    this.classifyId = this.$route.query.classifyId;
    this.queryVideoId = this.$route.query.videoId || "";
    this.getDetails();
  },
  methods: {
    formattedTime(val) {
      let minutes = Math.floor(val / 60);
      let remainingSeconds = val % 60;
      if (minutes > 0) {
        return `${minutes}分${remainingSeconds}秒`;
      } else {
        return `${remainingSeconds}秒`;
      }
    },
    getDetails() {
      courseInfoView(this.id).then((res) => {
        const data = res.data || {};

        // 极高内聚的兼容性适配垫片，将新数据模型字段透明转换为组件使用的旧字段
        data.details = data.videoList || []; // videoList -> details
        data.name = data.title || ""; // title -> name
        data.studyNum = data.viewCount || 0; // viewCount -> studyNum

        this.details = data;

        // 如果路由传入了特定的视频 ID，定位其在列表中的索引并作为初始播放项
        if (this.queryVideoId && data.details && data.details.length) {
          const index = data.details.findIndex(
            (item) => String(item.id) === String(this.queryVideoId)
          );
          if (index !== -1) {
            this.actIndex = index;
          }
        }

        if (this.details.details && this.details.details[this.actIndex]) {
          this.videoUrl = this.details.details[this.actIndex].fileUrl;
          this.videoId = this.details.details[this.actIndex].id;
          console.log("13", this.videoUrl);
          this.$refs.videoPlayer.load();
          this.showBtn = true;
        }
      });
    },
    changeAct(val) {
      if (val == this.actIndex) return;
      this.actIndex = val;
      this.videoUrl = this.details.details[this.actIndex].fileUrl;
      this.$refs.videoPlayer.load();
      this.showBtn = true;
      this.videoId = this.details.details[this.actIndex].id;
    },
    onPlay() {
      this.showBtn = false;
      this.isPlaying = true;
      this.handleMouseMove();
    },
    onPause() {
      this.showBtn = true;
      this.isPlaying = false;
      this.controlsVisible = true;
      if (this.controlsTimer) {
        clearTimeout(this.controlsTimer);
      }
      if ([0, 2].includes(this.details.details[this.actIndex].state)) {
        this.saveVideoTime();
      }
    },
    togglePlay() {
      const video = this.$refs.videoPlayer;
      if (video) {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      }
    },
    formatToHHMMSS(seconds) {
      if (!seconds) return "00:00:00";
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = seconds % 60;
      return [
        h.toString().padStart(2, "0"),
        m.toString().padStart(2, "0"),
        s.toString().padStart(2, "0"),
      ].join(":");
    },
    formatSeconds(value) {
      var secondsTime = parseInt(value); // 秒转成整数
      var minuteTime = 0; // 分
      var secondTime = 0; // 秒
      if (secondsTime > 60) {
        minuteTime = Math.floor(secondsTime / 60); // 获取分钟整数
        if (parseInt(secondsTime % 60) > 0) {
          secondTime = parseInt(secondsTime % 60);
          return minuteTime + "分" + secondTime + "秒";
        } else {
          return minuteTime + "分";
        }
      } else {
        return secondsTime + "秒";
      }
    },
    // 视频结束
    async onVideoEnded(e) {
      const currentVideo = this.details.details[this.actIndex];
      const totalDuration = currentVideo.durationNum;
      this.videoTime = totalDuration;

      this.getDetails();
      await this.saveVideoTime();
      await this.checkExam();
    },
    // 视频进度更新
    onTimeUpdate(e) {
      let currentTime = e.target.currentTime;
      this.videoTime = currentTime;
      this.videoCurrentTime = currentTime;
    },
    // 视频元数据加载完成，回显上次观看进度并还原播放倍速
    onLoadedMetadata(e) {
      e.target.playbackRate = this.currentPlaybackRate;
      this.videoDuration = e.target.duration || 0;
      const currentVideo = this.details.details[this.actIndex];
      if (
        currentVideo &&
        currentVideo.state != 1 &&
        currentVideo.viewTime > 0
      ) {
        e.target.currentTime = currentVideo.viewTime;
        this.videoCurrentTime = currentVideo.viewTime;
        console.log("回显上次观看时长:", currentVideo.viewTime);
      } else {
        e.target.currentTime = 0;
        this.videoCurrentTime = 0;
      }
    },
    formatTime(seconds) {
      if (isNaN(seconds) || seconds === null) return "00:00";
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = Math.floor(seconds % 60);
      if (h > 0) {
        return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
      }
      return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    },
    toggleMute() {
      this.isMuted = !this.isMuted;
      const video = this.$refs.videoPlayer;
      if (video) {
        video.muted = this.isMuted;
      }
    },
    toggleFullScreen() {
      const container = this.$refs.videoContainer;
      if (!container) return;

      if (!document.fullscreenElement) {
        container.requestFullscreen().then(() => {
          this.isFullScreen = true;
        }).catch((err) => {
          console.error("全屏失败:", err);
        });
      } else {
        document.exitFullscreen().then(() => {
          this.isFullScreen = false;
        });
      }
    },
    onFullScreenChange() {
      this.isFullScreen = !!document.fullscreenElement;
    },
    onProgressBarMouseDown(e) {
      this.isDraggingProgress = true;
      this.handleDragProgress(e);

      const moveHandler = (moveEvent) => {
        this.handleDragProgress(moveEvent);
      };

      const upHandler = () => {
        this.isDraggingProgress = false;
        window.removeEventListener("mousemove", moveHandler);
        window.removeEventListener("mouseup", upHandler);
      };

      window.addEventListener("mousemove", moveHandler);
      window.addEventListener("mouseup", upHandler);
    },
    handleDragProgress(e) {
      const rect = this.$refs.progressBar.getBoundingClientRect();
      const dragX = e.clientX - rect.left;
      const width = rect.width;
      let percent = dragX / width;
      if (percent < 0) percent = 0;
      if (percent > 1) percent = 1;

      const video = this.$refs.videoPlayer;
      if (video && this.videoDuration) {
        video.currentTime = percent * this.videoDuration;
        this.videoCurrentTime = video.currentTime;
      }
    },
    onProgressBarClick(e) {
      const rect = this.$refs.progressBar.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      let percent = clickX / width;
      if (percent < 0) percent = 0;
      if (percent > 1) percent = 1;

      const video = this.$refs.videoPlayer;
      if (video && this.videoDuration) {
        video.currentTime = percent * this.videoDuration;
        this.videoCurrentTime = video.currentTime;
      }
    },
    onVolumeTrackClick(e) {
      const rect = this.$refs.volumeTrack.getBoundingClientRect();
      const clickY = rect.bottom - e.clientY;
      const height = rect.height;
      let percent = clickY / height;
      if (percent < 0) percent = 0;
      if (percent > 1) percent = 1;

      this.volume = percent;
      this.isMuted = this.volume === 0;
      const video = this.$refs.videoPlayer;
      if (video) {
        video.volume = this.volume;
        video.muted = this.isMuted;
      }
    },
    onVolumeTrackMouseDown(e) {
      this.isDraggingVolume = true;
      this.handleDragVolume(e);

      const moveHandler = (moveEvent) => {
        this.handleDragVolume(moveEvent);
      };

      const upHandler = () => {
        this.isDraggingVolume = false;
        window.removeEventListener("mousemove", moveHandler);
        window.removeEventListener("mouseup", upHandler);
      };

      window.addEventListener("mousemove", moveHandler);
      window.addEventListener("mouseup", upHandler);
    },
    handleDragVolume(e) {
      const rect = this.$refs.volumeTrack.getBoundingClientRect();
      const dragY = rect.bottom - e.clientY;
      const height = rect.height;
      let percent = dragY / height;
      if (percent < 0) percent = 0;
      if (percent > 1) percent = 1;

      this.volume = percent;
      this.isMuted = this.volume === 0;
      const video = this.$refs.videoPlayer;
      if (video) {
        video.volume = this.volume;
        video.muted = this.isMuted;
      }
    },
    // 保存学习时长
    async saveStudyDuration() {
      try {
        const res = await videoStudyAdd({
          studyTime: this.studyDuration,
        });
        console.log("保存学习时长成功:", this.studyDuration);
      } catch (error) {
        console.error("保存学习时长失败:", error);
      }
    },
    // 保存已经观看多少秒视频
    async saveVideoTime() {
      const currentVideo = this.details.details[this.actIndex];
      const totalDuration = currentVideo.durationNum;
      console.log("当前视频的总时长:", totalDuration);
      console.log("当前观看时长:", this.videoTime);

      try {
        const res = await videoReadAdd({
          id: this.videoId,
          state: this.videoTime >= totalDuration ? 1 : 2,
          viewTime: this.videoTime,
        });
      } catch (error) {
        console.error("保存学习时长失败:", error);
      }
    },
    // 考试
    async checkExam() {
      const res = await videoStudy({ id: this.videoId });
      if (res.data.state === 1) {
        this.examModel = true;
      }
      console.log(res, "res");
    },
    jumpExam() {
      this.$router.push({
        path: `/examination/index?id=${this.id}`,
      });
    },
    /**
     * 变更视频播放倍速并立即应用到播放器
     * @param {number} rate - 目标播放速度倍率，如 1.0, 1.25, 1.5, 2.0
     * @returns {void}
     */
    changePlaybackRate(rate) {
      this.currentPlaybackRate = rate;
      const video = this.$refs.videoPlayer;
      if (video) {
        video.playbackRate = rate;
      }
    },
    /**
     * 鼠标在视频容器上移动时的交互处理（保持或展示控制组件）
     * @returns {void}
     */
    handleMouseMove() {
      this.controlsVisible = true;
      if (this.controlsTimer) {
        clearTimeout(this.controlsTimer);
      }
      // 若视频正在播放，鼠标静止3秒后自动隐藏控制栏
      if (this.isPlaying) {
        this.controlsTimer = setTimeout(() => {
          this.controlsVisible = false;
        }, 3000);
      }
    },
    /**
     * 鼠标离开视频容器时的交互处理（如在播放中则立即隐藏控制组件）
     * @returns {void}
     */
    handleMouseLeave() {
      this.showRateMenu = false;
      this.showVolumeSlider = false;
      if (this.isPlaying) {
        this.controlsVisible = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.school-details-page {
  width: 100%;
  min-height: 100vh;
  // background-color: #f9fafc;
  background-color: #fff;
  box-sizing: border-box;
}

/* Breadcrumb Subnav Header */
.details-sub-nav {
  width: 100%;
  height: 56px;
  background: #f3f6f8;
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom: 1px solid rgba(186, 202, 198, 0.15);
}

.breadcrumb-container {
  width: 1200px;
  padding: 0 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  text-align: left;
}

.breadcrumb-item {
  font-size: 14px;

  font-weight: 500;
  line-height: 20px;
  color: #3b4a47;
  cursor: pointer;
  &.active {
    color: #00d8c3;
    font-weight: 700;
  }
}

.breadcrumb-separator {
  width: 5px;
  height: 8px;
  background: rgba(59, 74, 71, 0.4);
  clip-path: polygon(0 0, 100% 50%, 0 100%);
  display: inline-block;
}

/* Details Page Main content body */
.details-content-wrap {
  width: 1200px;
  margin: 0 auto;
  padding: 40px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 40px;
}

/* Header Card (Video Left, Info Right) */
.details-header-card {
  width: 100%;
  height: 360px;
  background: #ffffff;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  border: 1px solid rgba(186, 202, 198, 0.2);
  box-sizing: border-box;
  display: flex;
  overflow: hidden;
}

.video-container {
  width: 636px;
  height: 358px;
  position: relative;
  background: #00161a;
  flex-shrink: 0;
}

.video-player {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0.4) 50%,
    rgba(0, 0, 0, 0) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.play-btn-icon {
  width: 80px;
  height: 80px;
  transition: transform 0.2s;
  &:hover {
    transform: scale(1.1);
  }
}

/* Right Info area styles */
.info-container {
  flex: 1;
  padding: 40px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: left;
}

.info-top {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-indicator {
  width: 6px;
  height: 20px;
  background: #00d8c3;
  border-radius: 9999px;
}

.info-title {
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #191c1e;
}

.info-desc {
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: rgba(59, 74, 71, 0.8);
}

.info-bottom {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.meta-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.meta-item {
  font-size: 14px;
  color: #3b4a47;
  .meta-label {
    color: #6b7a77;
  }
  .meta-value {
    font-weight: 700;
    color: #191c1e;
  }
}

.progress-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-text-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #6b7a77;
  .progress-ratio {
    color: #00d8c3;
    font-weight: 700;
  }
}

.progress-bar-bg {
  width: 100%;
  height: 6px;
  background: #f0f3f5;
  border-radius: 99px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: #00d8c3;
  border-radius: 99px;
  transition: width 0.4s ease;
}

.action-btn-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 22px;
}

/* 视频内悬浮倍速组件 */
.video-rate-control-wrap {
  position: relative; /* 作为向上滑出菜单的相对定位基准，参与控制栏水平 Flex 布局，防止重叠 */
  z-index: 10;
  cursor: pointer;
  user-select: none;
}

.rate-trigger-btn {
  height: 28px;
  line-height: 28px;
  padding: 0 12px;
  font-size: 13px;
  font-family: Inter, sans-serif;
  color: #ffffff;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  transition: all 0.2s ease;
  text-align: center;

  &:hover {
    color: #ffffff;
    background: rgba(0, 216, 195, 0.85); /* 悬浮时变为品牌青色 */
    border-color: rgba(255, 255, 255, 0.5);
    box-shadow: 0 0 10px rgba(0, 216, 195, 0.4);
  }
}

.rate-menu-panel {
  position: absolute;
  bottom: 34px; /* 向上弹出，防止底部越界遮挡 */
  left: 50%;
  transform: translateX(-50%);
  background: rgba(28, 28, 28, 0.95);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 6px 0;
  display: flex;
  flex-direction: column;
  min-width: 70px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.35);
  z-index: 20;
}

.rate-menu-item {
  height: 28px;
  line-height: 28px;
  text-align: center;
  font-size: 12px;
  font-family: Inter, sans-serif;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s ease;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.12);
  }

  &.active {
    color: #00d8c3;
    font-weight: 700;
  }
}

/* 动效过渡 */
.rate-menu-fade-enter-active,
.rate-menu-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.rate-menu-fade-enter,
.rate-menu-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px); /* 伴随轻微从下往上的滑入，符合向上弹出的逻辑 */
}

/* 自定义播放器控制条样式 */
.custom-video-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 52px;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.4) 65%, rgba(0, 0, 0, 0) 100%);
  padding: 0 16px 8px 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  z-index: 2147483647; /* 保证全屏下也位于最上层 */
}

/* 顶部进度滑块 */
.custom-progress-slider {
  width: 100%;
  height: 4px;
  position: relative;
  cursor: pointer;
  margin-bottom: 6px;
  border-radius: 99px;
  transition: height 0.2s;

  &:hover {
    height: 6px;
    .custom-progress-handle {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
}

.custom-progress-bg {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 99px;
}

.custom-progress-filled {
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: #00d8c3;
  border-radius: 99px;
  z-index: 2;
}

.custom-progress-handle {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ffffff;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%) scale(0);
  opacity: 0;
  transition: transform 0.15s ease, opacity 0.15s ease;
  z-index: 3;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.3);
}

/* 控制按钮行 */
.custom-controls-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 32px;
}

.controls-left, .controls-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.control-btn {
  color: #ffffff;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #00d8c3;
  }
}

.time-display {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-family: Inter, sans-serif;
  color: rgba(255, 255, 255, 0.85);
  user-select: none;
}

.time-separator {
  color: rgba(255, 255, 255, 0.4);
}

/* 自定义音量滑块 */
.custom-volume-control {
  position: relative;
  display: flex;
  align-items: center;
}

.volume-slider-panel {
  position: absolute;
  bottom: 34px;
  left: 50%;
  transform: translateX(-50%);
  width: 32px;
  height: 110px;
  background: rgba(21, 21, 21, 0.95);
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.4);
  z-index: 99;
}

.volume-track {
  width: 4px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  position: relative;
  border-radius: 99px;
  cursor: pointer;
}

.volume-fill {
  width: 100%;
  background: #00d8c3;
  position: absolute;
  bottom: 0;
  left: 0;
  border-radius: 99px;
}

.volume-handle {
  width: 10px;
  height: 10px;
  background: #ffffff;
  border-radius: 50%;
  position: absolute;
  left: 50%;
  transform: translate(-50%, 50%);
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.5);
  cursor: pointer;
}

/* 动效过渡 */
.controls-fade-enter-active,
.controls-fade-leave-active {
  transition: opacity 0.3s ease;
}
.controls-fade-enter,
.controls-fade-leave-to {
  opacity: 0;
}

.volume-fade-enter-active,
.volume-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.volume-fade-enter,
.volume-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(5px);
}

/* 针对 HTML5 video fullscreen 状态下自定义控制条的层级优化 */
.video-container:fullscreen {
  .custom-video-controls {
    z-index: 2147483647 !important;
  }
}

.start-learn-btn {
  padding: 10px 32px;
  background: #00d8c3;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0px 4px 6px rgba(0, 216, 195, 0.2);
  transition: background-color 0.2s, box-shadow 0.2s;

  &:hover {
    background: #00c4b0;
    box-shadow: 0px 6px 12px rgba(0, 216, 195, 0.3);
  }
}

/* Bottom Directory Section (Directory Listing) */
.directory-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-title {
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #191c1e;
}

.directory-list-card {
  width: 100%;
  background: #ffffff;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  border: 1px solid rgba(186, 202, 198, 0.3);
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.directory-tab-header {
  width: 100%;
  padding-left: 32px;
  padding-right: 32px;
  border-bottom: 1px solid rgba(186, 202, 198, 0.2);
  display: flex;
  box-sizing: border-box;
}

.directory-tab-item {
  padding: 20px 8px;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #00d8c3;
  border-bottom: 2px solid #00d8c3;
  box-sizing: border-box;
}

/* Tip boxes (Key points & Targets) */
.tips-row {
  padding: 32px;
  display: flex;
  gap: 24px;
  box-sizing: border-box;
}

.tip-box {
  flex: 1;
  padding: 16px;
  background: rgba(242, 244, 246, 0.4);
  border-radius: 12px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  text-align: left;
}

.tip-icon-wrap {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &.bg-teal {
    background: rgba(0, 216, 195, 0.1);
    color: #00d8c3;
  }

  i {
    font-size: 16px;
  }
}

.tip-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tip-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #191c1e;
}

.tip-desc {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: rgba(59, 74, 71, 0.8);
}

/* Chapter video items */
.chapter-list-wrap {
  padding: 0 32px 32px 32px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.chapter-item {
  width: 100%;
  padding: 24px 0;
  border-bottom: 1px solid #f0f3f5;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    .chapter-name {
      color: #00d8c3;
    }
  }

  &.active {
    .chapter-name {
      color: #00d8c3;
      font-weight: 700;
    }
    .play-circle-icon {
      transform: scale(1.1);
    }
  }
}

.chapter-left {
  display: flex;
  align-items: center;
  text-align: left;
  flex: 1;
  min-width: 0;
  margin-right: 24px;
}

.chapter-video-tag {
  font-size: 14px;
  color: #3b4a47;
  font-family: Inter, sans-serif;
  margin-right: 16px;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
}

.play-circle-icon {
  font-size: 24px;
  color: #00d8c3;
  display: inline-flex;
  align-items: center;
  margin-right: 16px;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.chapter-name {
  font-size: 16px;
  font-weight: 600;
  color: #191c1e;
  transition: color 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.chapter-right {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
}

.chapter-duration {
  font-size: 14px;
  font-family: Inter, sans-serif;
  color: #3b4a47;
}

.last-view-badge {
  padding: 4px 12px;
  background: #f2f4f6;
  border-radius: 6px;
  font-size: 12px;
  color: #3b4a47;

  &.completed {
    background: rgba(0, 216, 195, 0.1);
    color: #00d8c3;
  }
}

/* Exam modal content styles */
.examModelContent {
  padding: 30px;
  text-align: left;
  .examModelTitle {
    margin-bottom: 20px;
    font-weight: 700;
    font-size: 20px;
    color: #191c1e;
    line-height: 28px;
  }
  .examModelText {
    margin-bottom: 40px;
    font-size: 16px;
    color: #525f66;
    line-height: 24px;
  }
  .examModelBtn {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    .examModelBtnCancel {
      padding: 10px 24px;
      background: #ffffff;
      border: 1px solid #bacac6;
      font-size: 14px;
      color: #3b4a47;
      border-radius: 8px;
      cursor: pointer;
      text-align: center;
    }
    .examModelBtnStart {
      padding: 10px 24px;
      background: #00d8c3;
      font-weight: 700;
      font-size: 14px;
      color: #ffffff;
      border-radius: 8px;
      cursor: pointer;
      text-align: center;
      box-shadow: 0px 4px 6px rgba(0, 216, 195, 0.2);
    }
  }
}
</style>